import React from 'react'
import { useQuery } from '@tanstack/react-query'
import appConfigApi, { AppConfiguration } from '../lib/appConfigApi'
import { useFeatureFlags } from './useFeatureFlags'

// Icon mapping for sidebar items
import {
  DatabaseOutlined,
  RobotOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
  LineChartOutlined,
  SettingOutlined,
  ExperimentOutlined,
  AppstoreOutlined,
  FileOutlined,
  FolderOutlined,
  CloudServerOutlined,
} from '@ant-design/icons'

const iconMap: Record<string, any> = {
  DatabaseOutlined,
  RobotOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
  LineChartOutlined,
  SettingOutlined,
  ExperimentOutlined,
  AppstoreOutlined,
  FileOutlined,
  FolderOutlined,
  CloudServerOutlined,
}

export interface SidebarMenuItem {
  key: string
  icon: React.ReactNode
  label: string
  disabled?: boolean
  route?: string
  type?: 'item' | 'group' | 'divider'
}

export const useTrainingSidebarConfig = () => {
  const { isFeatureEnabled } = useFeatureFlags()

  const { data: sidebarConfig, isLoading, error, refetch } = useQuery({
    queryKey: ['training-sidebar-configuration'],
    queryFn: async () => {
      try {
        // Fetch training-specific configurations
        const configs = await appConfigApi.getConfigurationsByApplication('training')
        const sidebarConfigs = configs.filter(c => c.type === 'sidebar')

        console.log('Training sidebar config loaded:', sidebarConfigs)

        return {
          projectItems: sidebarConfigs.filter(c =>
            (c.metadata as any)?.category === 'project' || !c.position
          ),
          systemItems: sidebarConfigs.filter(c =>
            (c.metadata as any)?.category === 'system' || c.position === 'bottom'
          ),
          all: sidebarConfigs,
        }
      } catch (err) {
        console.error('Failed to load training sidebar config:', err)
        throw err
      }
    },
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
    retry: 2,
  })

  const processConfigurationItem = (config: AppConfiguration): SidebarMenuItem | null => {
    // Check feature flag if specified
    if (config.featureFlag && !isFeatureEnabled(config.featureFlag)) {
      return null
    }

    // Check if configuration is enabled and visible
    if (!config.isEnabled || !config.isVisible) {
      return null
    }

    // Process route - training app doesn't need project placeholder replacement
    const route = config.route || ''

    // Check if item requires specific conditions
    const disabled = config.config?.disabled === true

    return {
      key: route || `#${config.key}`,
      icon: config.icon && iconMap[config.icon] ? React.createElement(iconMap[config.icon]) : null,
      label: config.name,
      disabled,
      route,
      type: 'item',
    }
  }

  const projectMenuItems: SidebarMenuItem[] =
    sidebarConfig?.projectItems
      ?.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      ?.map(processConfigurationItem)
      .filter((item): item is SidebarMenuItem => item !== null) || []

  const systemMenuItems: SidebarMenuItem[] =
    sidebarConfig?.systemItems
      ?.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      ?.map(processConfigurationItem)
      .filter((item): item is SidebarMenuItem => item !== null) || []

  // Debug logging
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    console.log('Processed training sidebar items:', {
      projectMenuItems,
      systemMenuItems,
      rawConfig: sidebarConfig,
      isLoading,
      error
    })
  }

  return {
    projectMenuItems,
    systemMenuItems,
    isLoading,
    error,
    refetch,
  }
}
